<template>
  <div class="token-approval">

    <!-- Background -->
    <ui-wad class="overflow-hidden">
      <div class="token-approval__background" />
    </ui-wad>

    <ui-cloak
      :is-visible="mode !== MODE.PENDING"
      :idle-phase-duration="200"
      :sustain-phase-duration="400"
    >
      <template #cloak>
        <ui-wad class="d-flex align-items-center justify-content-center min-height-inherit">
          <ui-spinner
            class="token-approval__pending"
            :size="UI.SIZE.LARGE"
            :type="UI.SPINNER_TYPE.BORDER"
          />
        </ui-wad>
      </template>

      <template #default>
        <b-card
          :class="[
            'token-approval__body',
            'card--leveled'
          ]"
          no-body
        >

          <div
            v-if="mode === MODE.SUCCESS"
            class="token-approval__success"
          >

            <ui-wad class="d-flex justify-content-center">
              <div class="token-approval__header">
                <ui-icon
                  name="check"
                />
              </div>
            </ui-wad>

            <div class="token-approval__content">
              <p class="mb-3">
                Bonjour,
              </p>

              <p class="mb-2">
                <span class="font-weight-normal">
                  {{
                    alreadyDone ?
                      `Vous avez déjà validé` :
                      `Vous venez de valider`
                  }}
                </span>
                la demande de congés &amp; absences pour le salarié
                {{ collaborator }}
              </p>

              <ul>
                <li>
                  du {{ from_label }}
                  {{ start_time_label }}
                  au {{ to_label }}
                  {{ end_time_label }}
                </li>
                <li>
                  durée&nbsp;:&nbsp;
                  {{ days }}
                  {{
                    days >= 2 ? `jours` : `jour`
                  }}
                </li>
                <li>
                  nature du congé&nbsp;:&nbsp;
                  {{ leave_type_label }}
                </li>
              </ul>
            </div>

            <img
              :src="'/images/drawings/reposez-vous.png'"
              class="token-approval__drawings"
            >

          </div>

          <div
            v-if="mode === MODE.ERROR"
            class="token-approval__error"
          >

            <ui-wad class="d-flex justify-content-center">
              <div class="token-approval__header">
                <ui-icon
                  name="close"
                />
              </div>
            </ui-wad>

            <div class="token-approval__content">

              <p class="mb-2">
                <span class="font-weight-normal font-size-105">
                  La demande n'a pas abouti
                </span>
              </p>

              <p class="line-height-1.4 mb-1">
                Nous vous prions de bien vouloir recommencer l'opération dans quelques minutes,
                ou de contacter le support GHS.
              </p>

            </div>

          </div>

        </b-card>

        <a
          class="token-approval__logo-ghs"
          href="https://www.ghs.fr/"
          target="_blank"
        >
          <img :src="'/images/brand/logo_ghs_alice-blue.svg'">
        </a>

      </template>
    </ui-cloak>


  </div>
</template>

<style lang="sass" scoped>

$drawings-min-size: 260px
$logo-text-size: 100px
$width-md: map-get($breakpoints, 'tablet')

.token-approval
  @extend %fit-waterline

  $self: &

  background: linear-gradient(to bottom, $background-secondary, $background-tertiary)
  animation: fadeIn 600ms ease-in-out

  &__background
    @include no-interaction
    @include inset
    position: absolute
    background-size: cover
    opacity: .6
    background-image: image('/backgrounds/test.svg')
    background-position-x: -5vw

  &__body
    width: $width-md
    margin: 10vh auto 0
    position: relative

  &__pending
    color: $gray-bright

  ////////////////////////////////

  &__header
    $size: 52px

    border-radius: 50%
    width: $size
    height: $size
    display: flex
    justify-content: center
    align-items: center
    margin-top: 6vh
    margin-bottom: 6vh
    animation: bounceIn 600ms ease-in-out

    .ui-icon
      font-size: 28px
      color: $white

  &__success #{ $self }__header
    background-color: $color-green

  &__error #{ $self }__header
    background-color: $color-red

  ////////////////////////////////

  &__content
    width: 80%
    margin: 0 auto
    font-size: 18px
    font-weight: $font-weight-light
    opacity: 0
    animation: fadeIn 600ms ease-in-out 100ms forwards

    ul
      list-style-type: circle
      list-style-position: inside
      margin-left: space-x(2)
      font-size: 90%
      color: $black
      line-height: 1.8

  &__error #{ $self }__content
    margin-top: 2vh
    margin-bottom: 8vh

  ////////////////////////////////

  &__logo-ghs
    @include clickable
    display: block
    margin-left: auto
    margin-right: auto
    position: sticky
    top: 100vh
    padding-bottom: space-x(3)
    width: $logo-text-size

  &__drawings
    width: 40%
    min-width: $drawings-min-size
    margin-left: auto
    margin-right: auto
    opacity: 0
    animation: fadeIn 600ms ease-in-out 300ms forwards

  ////////////////////////////////

  /// Responsive
  ///

  @include media('<laptop')
    background: $card-bg

    &__background
      display: none

    &__body
      margin-top: initial
      border: initial
      box-shadow: initial

  @include media('<tablet')

    &__body
      width: initial

</style>

<script lang="ts">
import type { LocationQuery } from 'vue-router'
import { useLegacyPageFetch } from '~/composables/core/hook'
import Console from '~/utils/lib/core/console'
import { join, path, propOr, tapCatch } from '~/utils/lib/core/prelude'
import { useStore_transat_leaveRequest_details } from '~/stores/transat/leave-request/details'

const MODE = {
  PENDING: 'mode_pending',
  ERROR: 'mode_error',
  SUCCESS: 'mode_success',
}

const console = new Console('component:token-approval')

export default defineNuxtComponent({
  setup() {
    definePageMeta({
      layout: 'blank'
    })

    const {
      fetchState,
      fetchWrapper,
    } = useLegacyPageFetch()

    const store_leaveRequest_details = useStore_transat_leaveRequest_details()

    return {
      fetchState,
      fetchWrapper,
      store_leaveRequest_details,
    }
  },
  data() {
    return {
      // helpers
      MODE,
      mode: MODE.PENDING,
      // lazy hydrated template date
      alreadyDone: false,
      collaborator: '',
      from_label: '',
      to_label: '',
      start_time_label: '',
      end_time_label: '',
      days: 0,
      leave_type_label: '',
    }
  },
  created() {
    this.fetchWrapper(
      () => this.fetch()
    )
  },
  methods: {
    fetch() {
      const uuidData      = propOr<string, LocationQuery, string>('', 'uuidData', this.$route.query)
      const code_societe  = propOr<string, LocationQuery, string>('', 'code_societe', this.$route.query)
      const token         = propOr<string, LocationQuery, string>('', 'token', this.$route.query)

      return this.store_leaveRequest_details.tokenApproval(
          { uuidData, code_societe, token }
        )
        .then(results => {
          this.alreadyDone = path([ 'leave', 'already_responded'], results)
          this.collaborator = join(' ', [
            path([ 'salarie', 'prenom' ], results),
            path([ 'salarie', 'nom' ], results)
          ])
          this.from_label = path([ 'leave', 'from' ], results)
          this.to_label = path([ 'leave', 'to'], results)
          this.start_time_label = path([ 'leave', 'start_time' ], results)
          this.end_time_label = path([ 'leave', 'end_time' ], results)
          this.days = path([ 'leave', 'days' ], results)
          this.leave_type_label = path([ 'leave', 'leave_type' ], results)

          this.mode = MODE.SUCCESS
        })
        .catch(tapCatch(err => {
          console.warn('fetch token', err)

          this.mode = MODE.ERROR
        }))
    }
  }
})
</script>
